import {
  Button,
  CircularProgress,
  TextField,
  useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { SendHorizontal } from "lucide-react";
import React, { FormEvent, RefObject, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, InputPill } from "./AISupportChatMessageInput.styles";

interface AISupportChatMessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  inputRef: RefObject<HTMLInputElement>;
}

const AISupportChatMessageInput = ({
  onSendMessage,
  disabled,
  isLoading,
  inputRef
}: AISupportChatMessageInputProps) => {
  const theme = useTheme();
  const [message, setMessage] = useState("");
  const { t } = useTranslation();

  const sendMessage = (e?: FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    if (!message) return;
    setMessage("");
    onSendMessage(message);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.altKey && !e.shiftKey) {
      sendMessage();
      e.preventDefault();
    }
  };

  return (
    <Container gap={ "10px" }>
      <InputPill theme={ theme }>
        <form onSubmit={ sendMessage } style={ { width: "100%" } }>
          <TextField
            disabled={ disabled }
            inputRef={ inputRef }
            variant={ "standard" }
            placeholder={ t("ai_support_chat_input_ph") || "" }
            size={ "small" }
            value={ message }
            sx={ { padding: 0, width: "100%" } }
            multiline={ true }
            maxRows={ 2 }
            onKeyDown={ (event) => handleInputKeyDown(event) }
            onChange={ (event) => setMessage(event.target.value) }
            InputProps={ {
              disableUnderline: true,
              sx: {
                color: theme.customColors.textPrimaryAlternative,
                padding: 0
              }
            } }
          />
        </form>
      </InputPill>
      <Button
        onClick={ () => sendMessage() }
        disabled={ !message || disabled || isLoading }
        endIcon={ isLoading
          ? <CircularProgress size={ 16 } />
          : <SendHorizontal size={ 16 } /> }
        sx={ {
          "&.Mui-disabled": {
            backgroundColor: alpha(theme.customColors.systemSecondary01, 0.3),
            color: alpha(theme.customColors.textPrimaryCta, 0.5)
          },
          "&:hover": {
            backgroundColor: theme.customColors.systemSecondary01Hover
          },
          alignSelf: "stretch",
          backgroundColor: theme.customColors.systemSecondary01,
          borderRadius: "8px",
          color: theme.customColors.textPrimaryCta,
          flexShrink: 0,
          fontWeight: 700,
          paddingLeft: "20px",
          paddingRight: "20px",
          textTransform: "none",
          whiteSpace: "nowrap"
        } }
      >
        { t("ai_support_chat_send") }
      </Button>
    </Container>
  );
};

export default AISupportChatMessageInput;
